/**
 * DataSourceBadge — inline status indicator for metric provenance
 *
 * 🟢 Live Data   |  🟡 Partial Data   |  ⚪ Not Connected
 */
import React from 'react';
import type { DataSourceStatus } from '../../../types/insightsTypes';

interface DataSourceBadgeProps {
  status: DataSourceStatus;
  /** Optional view/source name shown in dev tooltip */
  source?: string;
  /** Compact mode — dot only, no text */
  compact?: boolean;
}

const STATUS_CONFIG: Record<DataSourceStatus, { dot: string; text: string; label: string }> = {
  live: {
    dot: 'bg-green-500',
    text: 'text-green-700',
    label: 'Live Data',
  },
  partial: {
    dot: 'bg-amber-500',
    text: 'text-amber-700',
    label: 'Partial Data',
  },
  not_connected: {
    dot: 'bg-slate-400',
    text: 'text-slate-500',
    label: 'Not Connected',
  },
};

const DataSourceBadge: React.FC<DataSourceBadgeProps> = ({
  status,
  source,
  compact = false,
}) => {
  const config = STATUS_CONFIG[status];

  return (
    <span
      className="inline-flex items-center gap-1.5"
      title={source && import.meta.env.DEV ? `Source: ${source}` : undefined}
    >
      <span className={`inline-block w-2 h-2 rounded-full ${config.dot}`} />
      {!compact && (
        <span className={`text-xs font-medium ${config.text}`}>
          {config.label}
        </span>
      )}
    </span>
  );
};

export default DataSourceBadge;
